/**
 * Site Footer Component - Edem AI
 *
 * Premium dark footer with gradient accents, organized link columns,
 * social links, payment badges, and 18+ disclaimer.
 */
import Image from 'next/image';
import Link from 'next/link';

import { Globe, Instagram, Shield, Sparkles } from 'lucide-react';

// The two "Explore" columns are the site's ONLY internal links to the SEO
// landing pages — GSC showed those pages producing 57.8% of all organic
// clicks while having zero inbound internal links (sitemap-only discovery).
const footerSections = [
  {
    title: 'Features',
    links: [
      { label: 'Generate Images', href: '/generate' },
      { label: 'AI Chat', href: '/chat', noFollow: true },
      { label: 'Create Character', href: '/create' },
      { label: 'Catalog', href: '/catalog' },
    ],
  },
  {
    title: 'AI Chat',
    links: [
      { label: 'AI Adult Chat', href: '/ai-adult-chat' },
      { label: 'AI Chat 18+', href: '/ai-chat-18-plus' },
      { label: 'Uncensored AI Chat', href: '/ai-uncensored-chat' },
      { label: 'AI Sexy Chat', href: '/ai-sexy-chat' },
      { label: 'AI Erotic Chat', href: '/ai-erotic-chat' },
      { label: 'AI Dirty Talk', href: '/ai-dirty-talk-chat' },
      { label: 'AI Roleplay Chat', href: '/ai-roleplay-chat' },
      { label: 'AI Nude Chat', href: '/ai-nude-chat' },
      { label: 'AI Naked Chat', href: '/ai-naked-chat' },
      { label: 'AI JOI Chat', href: '/ai-jerk-off-chat' },
    ],
  },
  {
    title: 'AI Girlfriend',
    links: [
      { label: 'AI Girlfriend Generator', href: '/ai-girlfriend-generator' },
      { label: 'NSFW AI Girlfriend', href: '/nsfw-ai-girlfriend-18-plus' },
      { label: 'AI Sexy Girlfriend', href: '/ai-sexy-girlfriend' },
      { label: 'AI BDSM Generator', href: '/ai-bdsm-generator' },
      { label: 'AI Bondage Generator', href: '/ai-bondage-generator' },
      { label: 'AI Dominatrix', href: '/ai-dominatrix-generator' },
      { label: 'AI Slut Generator', href: '/ai-slut-generator' },
    ],
  },
  {
    title: 'Discover',
    links: [
      { label: 'Trending', href: '/catalog?sort=trending' },
      { label: 'New Characters', href: '/catalog?sort=new' },
      { label: 'Top Rated', href: '/catalog?sort=top' },
      { label: 'Pricing', href: '/pricing' },
      { label: 'Blog', href: '/blog' },
      { label: 'About', href: '/about' },
    ],
  },
  {
    title: 'Support',
    links: [
      { label: 'Help Center', href: '/help' },
      { label: 'FAQ', href: '/faq' },
      { label: 'Contact Us', href: '/contact' },
      { label: 'Complaints', href: '/complaints' },
    ],
  },
  {
    title: 'Legal',
    links: [
      { label: 'Terms of Service', href: '/terms-of-service' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: '2257 Compliance', href: '/2257-compliance' },
    ],
  },
];

// Only live, brand-owned profiles — dead '#' placeholders (Telegram/Discord)
// were removed; they rendered real <a> tags going nowhere on every page.
const socialLinks = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/edemaiofficial/',
    icon: Instagram,
  },
];

export function SiteFooter() {
  return (
    <footer className="relative overflow-hidden bg-black">
      {/* Top gradient line */}
      <div className="via-eros-pink/50 h-px bg-gradient-to-r from-transparent to-transparent" />

      {/* Ambient glow */}
      <div className="bg-eros-purple/[0.04] pointer-events-none absolute top-0 left-1/2 h-[200px] w-[600px] -translate-x-1/2 rounded-full blur-3xl" />

      <div className="relative container mx-auto px-4 sm:px-6">
        {/* CTA Banner */}
        <div className="py-10 sm:py-14">
          <div className="relative overflow-hidden rounded-2xl">
            {/* Banner background */}
            <div className="from-eros-purple/20 via-eros-pink/15 to-eros-orange/10 absolute inset-0 rounded-2xl bg-gradient-to-r" />
            <div className="absolute inset-0 rounded-2xl border border-white/[0.06]" />

            <div className="relative flex flex-col items-center justify-between gap-6 px-6 py-8 sm:flex-row sm:px-10 sm:py-10">
              <div className="text-center sm:text-left">
                <h3 className="mb-2 flex items-center justify-center gap-2 text-xl font-bold text-white sm:justify-start sm:text-2xl">
                  <Sparkles className="text-eros-pink h-5 w-5" />
                  Create your dream AI companion
                </h3>
                <p className="max-w-lg text-sm text-gray-400 sm:text-base">
                  Design unique characters, generate stunning images, and chat
                  with intelligent AI personalities.
                </p>
              </div>
              <Link
                href="/create"
                className="from-eros-pink to-eros-purple shrink-0 rounded-xl bg-gradient-to-r px-8 py-3 text-sm font-semibold text-white transition-all hover:shadow-[0_0_30px_rgba(236,72,153,0.3)] sm:text-base"
              >
                Get Started Free
              </Link>
            </div>
          </div>
        </div>

        {/* Main Footer Grid */}
        <div className="grid grid-cols-2 gap-8 pb-10 sm:gap-10 sm:pb-12 md:grid-cols-4 lg:grid-cols-8">
          {/* Brand Column */}
          <div className="col-span-2">
            <Link href="/" className="inline-flex items-center gap-2">
              {/* Same logo as the sidebar / navbar */}
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/images/favicon/logo-64.webp"
                width={64}
                height={64}
                alt="Edem AI"
                className="h-8 w-8 flex-shrink-0"
              />
              <span
                className="text-2xl leading-none font-semibold tracking-tight whitespace-nowrap sm:text-3xl"
                style={{ fontFamily: 'var(--font-display), Georgia, serif' }}
              >
                <span className="text-white">Edem</span>{' '}
                <span style={{ color: '#b07cf0' }}>AI</span>
              </span>
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-gray-500">
              Explore infinite possibilities with AI companions. Create, chat,
              and generate stunning content with cutting-edge AI.
            </p>

            {/* Social Links */}
            <div className="mt-5 flex items-center gap-2">
              {socialLinks.map((social) => (
                <a
                  key={social.label}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  // 44px hit area (a11y "tap targets too small") while the
                  // visible chip stays 36px via the inner span.
                  className="flex h-11 w-11 items-center justify-center"
                  aria-label={social.label}
                  title={social.label}
                >
                  <span className="hover:border-eros-pink/30 flex h-9 w-9 items-center justify-center rounded-xl border border-white/[0.06] bg-white/[0.04] text-gray-400 transition-all hover:bg-white/[0.08] hover:text-white">
                    <social.icon className="h-4 w-4" aria-hidden="true" />
                  </span>
                </a>
              ))}
              <a
                href="mailto:support@edemai.org"
                className="ml-2 text-xs text-gray-500 transition-colors hover:text-gray-300"
              >
                support@edemai.org
              </a>
            </div>
          </div>

          {/* Link Columns */}
          {footerSections.map((section) => (
            <div key={section.title}>
              <h4 className="mb-4 text-sm font-semibold text-white">
                {section.title}
              </h4>
              <ul className="space-y-2.5">
                {section.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      {...('noFollow' in link && link.noFollow
                        ? { rel: 'nofollow' }
                        : {})}
                      className="text-sm text-gray-500 transition-colors hover:text-gray-200"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Divider */}
        <div className="h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />

        {/* Bottom Bar */}
        <div className="py-6 sm:py-8">
          <div className="flex flex-col items-center justify-between gap-5 lg:flex-row">
            {/* Left: Copyright */}
            <div className="flex flex-col items-center gap-3 text-center sm:flex-row sm:text-left">
              <p className="text-xs text-gray-500 sm:text-sm">
                {/* Full legal entity + address: corporate identity used to be
                    confined to 4 legal pages — a trust signal Google's QRG
                    weighs heavily for paid adult services. */}
                &copy; {new Date().getFullYear()} Edem AI LLC, 30 N Gould St Ste
                R, Sheridan, WY 82801, USA. All rights reserved.
              </p>
              <span className="hidden text-gray-700 sm:inline">&middot;</span>
              <button className="flex items-center gap-1.5 text-xs text-gray-500 transition-colors hover:text-gray-300 sm:text-sm">
                <Globe className="h-3.5 w-3.5" />
                English
              </button>
            </div>

            {/* Center: Payment Methods */}
            <div className="flex items-center gap-3">
              <span className="mr-1 text-xs text-gray-600">We accept</span>
              <div className="flex items-center gap-2 rounded-lg border border-white/[0.05] bg-white/[0.03] px-3 py-1.5">
                <Image
                  src="/images/payment/visa.svg"
                  alt="Visa"
                  width={36}
                  height={24}
                  className="h-5 w-auto opacity-60"
                />
                <div className="h-4 w-px bg-white/10" />
                <Image
                  src="/images/payment/mastercard.svg"
                  alt="Mastercard"
                  width={36}
                  height={24}
                  className="h-5 w-auto opacity-60"
                />
                <div className="h-4 w-px bg-white/10" />
                <Image
                  src="/images/payment/apple-pay.svg"
                  alt="Apple Pay"
                  width={36}
                  height={24}
                  className="h-5 w-auto opacity-60"
                />
                <div className="h-4 w-px bg-white/10" />
                <Image
                  src="/images/payment/google-pay.svg"
                  alt="Google Pay"
                  width={36}
                  height={24}
                  className="h-5 w-auto opacity-60"
                />
              </div>
            </div>

            {/* Right: 18+ Badge */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 rounded-lg border border-red-500/20 bg-red-500/10 px-3 py-1.5">
                <Shield className="h-3.5 w-3.5 text-red-400" />
                <span className="text-xs font-semibold text-red-400">18+</span>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="mt-5 border-t border-white/[0.04] pt-5">
            <p className="mx-auto max-w-3xl text-center text-[11px] leading-relaxed text-gray-600 sm:text-xs">
              Edem AI is an entertainment platform intended for adults 18 years
              and older. All characters, images, and interactions are entirely
              fictional and AI-generated. No real individuals are depicted. By
              using this site, you confirm that you are of legal age in your
              jurisdiction.
            </p>
          </div>
        </div>
      </div>

      {/* Bottom gradient fade */}
      <div className="via-eros-purple/30 h-px bg-gradient-to-r from-transparent to-transparent" />
    </footer>
  );
}
